<template>
    <v-container fluid fill-height>
        <Headful title="Evaluatie"></Headful>
        <Header color="white" show-logo :dark="false" extended></Header>
        <v-layout align-center justify-center>
            <v-flex>
                <p v-if="loading" class="text-center my-7">
                    <v-icon large color="primary">fal fa-spinner-third fa-spin</v-icon>
                </p>
                <v-alert v-else-if="notFound" dark color="error" class="text-center my-7">
                    Deze link is niet (meer) geldig.
                </v-alert>
                <div v-else class="px-5">
                    <v-card class="elevation-0 mx-auto" max-width="720">
                        <v-card-text class="pa-7">
                            <h1 class="text-h5 mb-5 text-center">Evaluatie Diensten vanuit OWR Drechtsteden/West-Brabant</h1>

                            <!-- Dienst en categorie komen uit het dienstenportaal, die hoeft de gebruiker niet zelf in te vullen. -->
                            <v-simple-table class="mb-5">
                                <tbody>
                                    <tr><td><strong>Dienst</strong></td><td>{{ request.organisationName }}</td></tr>
                                    <tr><td><strong>Type dienst</strong></td><td>{{ request.typeDienst }}</td></tr>
                                    <tr><td><strong>Schoolbestuur</strong></td><td>{{ request.schoolbestuurName }}</td></tr>
                                </tbody>
                            </v-simple-table>

                            <v-alert v-if="success" color="success" class="mb-0">
                                Bedankt voor je evaluatie!
                            </v-alert>
                            <v-alert v-else-if="request.evaluated" color="warning" class="mb-0">
                                De evaluatie is al ingevuld. Bedankt!
                            </v-alert>
                            <v-form v-else ref="form" class="evaluation-form" @submit.prevent="submit" :disabled="submitting">
                                <p>
                                    Bedankt dat je gebruik hebt gemaakt van <strong>{{ request.organisationName }}</strong>.
                                    We horen graag hoe je deze dienst hebt ervaren en wat deze je heeft opgeleverd. Met jouw
                                    feedback kunnen we het aanbod van de Onderwijsregio verder verbeteren en goed laten
                                    aansluiten bij de behoeften van onderwijsprofessionals.
                                </p>
                                <p class="mb-6">
                                    Het invullen duurt ongeveer 2 minuten. Je antwoorden worden vertrouwelijk verwerkt.
                                </p>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': needsFitError }">1. In hoeverre sloot deze dienst aan bij jouw behoefte?</p>
                                <v-radio-group v-model="form.needsFit" class="mt-0 mb-4" hide-details="auto" :rules="[requiredRule]">
                                    <v-radio v-for="option in needsFitOptions" :key="option.value"
                                        :label="option.value + '. ' + option.label" :value="option.value"
                                    ></v-radio>
                                </v-radio-group>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': qualityError }">2. Hoe beoordeel je de kwaliteit van de dienst?</p>
                                <v-radio-group v-model="form.quality" class="mt-0 mb-4" hide-details="auto" :rules="[requiredRule]">
                                    <v-radio v-for="option in qualityOptions" :key="option.value"
                                        :label="option.value + '. ' + option.label" :value="option.value"
                                    ></v-radio>
                                </v-radio-group>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': professionalismError }">3. In hoeverre ben je tevreden over de professionaliteit van de dienstverlener?</p>
                                <v-radio-group v-model="form.professionalism" class="mt-0 mb-4" hide-details="auto" :rules="[requiredRule]">
                                    <v-radio v-for="option in professionalismOptions" :key="option.value"
                                        :label="option.value + '. ' + option.label" :value="option.value"
                                    ></v-radio>
                                </v-radio-group>

                                <p class="font-weight-bold mb-0" :class="{ 'error--text': outcomesError }">4. Wat heeft deze dienst jou vooral opgeleverd?</p>
                                <p class="text-caption mb-1">Meerdere antwoorden mogelijk.</p>
                                <v-checkbox v-for="option in outcomeOptions" :key="option.value"
                                    v-model="form.outcomes" :value="option.value" :label="option.label"
                                    class="mt-0" hide-details
                                ></v-checkbox>
                                <v-text-field v-if="otherSelected" outlined rounded hide-details="auto" class="mt-3"
                                    label="Anders, namelijk" v-model="form.outcomeOther"
                                    :maxlength="maxOpenAnswerLength" :rules="[requiredRule]"
                                ></v-text-field>
                                <v-alert v-if="outcomesError" dense text color="error" class="mt-3 mb-0">
                                    Kies minimaal één antwoord.
                                </v-alert>
                                <div class="mb-6"></div>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': mainResultError }">5. Wat is voor jou het belangrijkste resultaat van deze dienst?</p>
                                <p class="text-caption mb-1">
                                    Denk bijvoorbeeld aan iets wat je nu weet, kunt, anders doet of verder wilt ontwikkelen.
                                </p>
                                <v-textarea outlined rounded class="mb-4" rows="3"
                                    v-model="form.mainResult" :counter="maxOpenAnswerLength"
                                    :maxlength="maxOpenAnswerLength" :rules="[requiredRule]"
                                ></v-textarea>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': appreciatedError }">6. Wat heb je het meest gewaardeerd aan de dienst en/of de dienstverlener?</p>
                                <v-textarea outlined rounded class="mb-4" rows="3"
                                    v-model="form.appreciated" :counter="maxOpenAnswerLength"
                                    :maxlength="maxOpenAnswerLength" :rules="[requiredRule]"
                                ></v-textarea>

                                <p class="font-weight-bold mb-1" :class="{ 'error--text': ratingError }">7. Welk rapportcijfer geef je deze dienst als geheel?</p>
                                <p class="text-caption mb-1">1 = zeer slecht, 10 = uitstekend.</p>
                                <v-text-field outlined rounded hide-details="auto" class="mb-6"
                                    label="Cijfer (1 t/m 10)" type="number" min="1" max="10" step="1"
                                    v-model.number="form.rating" :rules="ratingRules"
                                ></v-text-field>

                                <p class="font-weight-bold mb-1">8. Heb je één concrete tip waarmee we deze dienst kunnen verbeteren?</p>
                                <v-textarea outlined rounded hide-details="auto" class="mb-4" rows="3"
                                    label="Optioneel" v-model="form.tip"
                                    :counter="maxOpenAnswerLength" :maxlength="maxOpenAnswerLength"
                                ></v-textarea>

                                <v-btn type="submit" color="primary" rounded block class="elevation-0" :loading="submitting" :disabled="submitting">
                                    Evaluatie versturen
                                </v-btn>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </div>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Headful from "@/components/layout/Headful";
import Header from "@/components/layout/Header";

// De sleutels van vraag 4 komen overeen met EvaluationQuestions::OUTCOME_LABELS in de API.
const OUTCOME_OTHER = 'anders';

export default {
    name: "EvaluateRequest",
    components: { Headful, Header },
    props: {
        token: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        loading: true,
        notFound: false,
        submitting: false,
        success: false,
        showErrors: false,
        request: {},
        maxOpenAnswerLength: 300,
        form: {
            needsFit: null,
            quality: null,
            professionalism: null,
            outcomes: [],
            outcomeOther: null,
            mainResult: null,
            appreciated: null,
            rating: null,
            tip: null,
        },
        needsFitOptions: [
            { value: 1, label: 'Helemaal niet' },
            { value: 2, label: 'Enigszins' },
            { value: 3, label: 'Voldoende' },
            { value: 4, label: 'Goed' },
            { value: 5, label: 'Zeer goed' },
        ],
        qualityOptions: [
            { value: 1, label: 'Zeer onvoldoende' },
            { value: 2, label: 'Onvoldoende' },
            { value: 3, label: 'Voldoende' },
            { value: 4, label: 'Goed' },
            { value: 5, label: 'Zeer goed' },
        ],
        professionalismOptions: [
            { value: 1, label: 'Zeer ontevreden' },
            { value: 2, label: 'Ontevreden' },
            { value: 3, label: 'Neutraal' },
            { value: 4, label: 'Tevreden' },
            { value: 5, label: 'Zeer tevreden' },
        ],
        outcomeOptions: [
            { value: 'nieuwe_kennis', label: 'Nieuwe kennis of inzichten' },
            { value: 'praktische_handvatten', label: 'Praktische handvatten die ik kan toepassen' },
            { value: 'meer_vertrouwen', label: 'Meer vertrouwen in mijn eigen handelen' },
            { value: 'professionele_groei', label: 'Groei in mijn professionele vaardigheden' },
            { value: 'werkplezier', label: 'Meer werkplezier, energie of veerkracht' },
            { value: 'ondersteuning_vraag', label: 'Ondersteuning bij een concrete vraag of uitdaging' },
            { value: 'nieuwe_contacten', label: 'Nieuwe contacten of waardevolle uitwisseling' },
            { value: 'volgende_stap', label: 'Duidelijkheid over een volgende stap' },
            { value: OUTCOME_OTHER, label: 'Anders, namelijk' },
            { value: 'onvoldoende_opgeleverd', label: 'De dienst heeft mij nog onvoldoende opgeleverd' },
        ],
        requiredRule: (value) => !!value || "Dit veld is verplicht.",
        ratingRules: [
            (value) => (null !== value && '' !== value) || "Dit veld is verplicht.",
            (value) => (Number.isInteger(Number(value)) && value >= 1 && value <= 10) || "Geef een heel cijfer van 1 t/m 10.",
        ],
    }),
    computed: {
        otherSelected() {
            return this.form.outcomes.includes(OUTCOME_OTHER);
        },
        needsFitError() {
            return this.showErrors && !this.form.needsFit;
        },
        qualityError() {
            return this.showErrors && !this.form.quality;
        },
        professionalismError() {
            return this.showErrors && !this.form.professionalism;
        },
        outcomesError() {
            return this.showErrors && !this.form.outcomes.length;
        },
        mainResultError() {
            return this.showErrors && !this.form.mainResult;
        },
        appreciatedError() {
            return this.showErrors && !this.form.appreciated;
        },
        ratingError() {
            const rating = this.form.rating;

            return this.showErrors && !(Number.isInteger(Number(rating)) && rating >= 1 && rating <= 10);
        },
    },
    mounted() {
        this.$store.dispatch('request/fetchEvaluation', { token: this.token })
            .then(request => { this.request = request; })
            .catch(e => {
                console.error(e);
                this.notFound = true;
            })
            .finally(() => { this.loading = false; });
    },
    methods: {
        submit() {
            // De meerkeuzevraag heeft geen rules; die valt buiten de formulier-validatie.
            this.showErrors = true;

            const valid = this.$refs.form.validate();

            if (!valid || this.outcomesError || this.submitting) {
                this.scrollToFirstError();

                return;
            }

            this.submitting = true;

            this.$store.dispatch('request/submitEvaluation', {
                token: this.token,
                ...this.form,
                outcomeOther: this.otherSelected ? this.form.outcomeOther : null,
            })
                .then(() => { this.success = true; })
                .catch(e => {
                    console.error(e);
                    this.$store.dispatch('showSnackbar', { color: 'error', text: 'De evaluatie kon niet worden opgeslagen' });
                })
                .finally(() => { this.submitting = false; });
        },
        scrollToFirstError() {
            this.$nextTick(() => {
                const firstError = this.$el.querySelector('.error--text');

                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
    },
}
</script>

<style>
/* Bij een validatiefout kleurt de vraag zelf rood; de antwoordopties houden hun normale kleur. */
.evaluation-form .v-input--radio-group.error--text .v-radio .v-label {
    color: rgba(0, 0, 0, 0.87);
}
.evaluation-form .v-input--radio-group.error--text .v-radio .v-icon {
    color: rgba(0, 0, 0, 0.54);
}
</style>
